import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteFooter } from "@/components/site-footer";
import { detectSeoLocale } from "@/lib/seo-locale";
import { hreflangLinks, seoMeta, SITE_URL, DEFAULT_SEO_LOCALE } from "@/lib/seo";

export const Route = createFileRoute("/privacy")({
  loader: async () => ({ lang: await detectSeoLocale() }),
  head: ({ loaderData }) => ({
    meta: seoMeta("privacy", loaderData?.lang ?? DEFAULT_SEO_LOCALE, {
      path: "/privacy",
      ogType: "article",
      robots: "index, follow",
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/privacy` }, ...hreflangLinks("/privacy")],
  }),
  component: PrivacyPage,
});

function PrivacyPage() {
  return (
    <div className="min-h-dvh bg-background">
      <main className="mx-auto max-w-2xl px-5 py-10">
        <p className="text-xs text-muted-foreground"><Link to="/" className="hover:underline">← Torna a Lumen</Link></p>
        <h1 className="mt-4 text-3xl font-bold tracking-tight">Informativa sulla Privacy</h1>
        <p className="mt-2 text-sm text-muted-foreground">Ultimo aggiornamento: 6 luglio 2026</p>

        <div className="prose prose-sm mt-6 max-w-none space-y-6 text-sm leading-relaxed text-foreground">
          <section>
            <h2 className="text-lg font-semibold">1. Titolare del trattamento</h2>
            <p>
              Il titolare del trattamento dei dati è <strong>Lerna AI</strong> ("Lerna AI", "noi"), fornitore dell'app Lumen. Puoi contattarci per questioni privacy tramite l'assistenza in-app.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold">2. Categorie di dati raccolti</h2>
            <ul className="list-disc pl-5">
              <li><strong>Account:</strong> nome, indirizzo email, password (in forma cifrata), foto profilo se fornita.</li>
              <li><strong>Profilo di studio:</strong> classe, materie, obiettivi, preferenze (es. modalità DSA, focus mode).</li>
              <li><strong>Contenuti utente:</strong> messaggi al tutor, appunti, immagini, audio caricati, flashcard, mappe e quiz generati.</li>
              <li><strong>Uso e telemetria:</strong> pagine visitate, funzioni utilizzate, tempi di sessione, log di errore, identificatori del dispositivo, indirizzo IP.</li>
              <li><strong>Assistenza:</strong> messaggi che ci invii per supporto.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold">3. Finalità e basi giuridiche</h2>
            <ul className="list-disc pl-5">
              <li>Erogazione del servizio e del tuo account — <em>esecuzione del contratto</em>.</li>
              <li>Elaborazione dei contenuti tramite modelli IA per generare risposte, mappe, quiz — <em>esecuzione del contratto</em>.</li>
              <li>Sicurezza, prevenzione frodi, miglioramento del prodotto — <em>legittimo interesse</em>.</li>
              <li>Assistenza clienti — <em>esecuzione del contratto</em>.</li>
              <li>Comunicazioni di marketing — solo previo tuo <em>consenso</em>, revocabile in qualsiasi momento.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold">4. Destinatari dei dati (sub-responsabili)</h2>
            <ul className="list-disc pl-5">
              <li><strong>Fornitori di hosting e infrastruttura</strong> (cloud, database, storage, edge functions).</li>
              <li><strong>Fornitori di modelli IA</strong> per elaborare le tue richieste (chat, generazione di contenuti, trascrizione audio, OCR, sintesi vocale).</li>
              <li><strong>Strumenti analitici e di monitoraggio errori</strong> per capire l'uso del prodotto e diagnosticare problemi.</li>
              <li><strong>Consulenti professionali</strong> (legali, contabili) e <strong>autorità</strong> quando richiesto dalla legge.</li>
            </ul>
            <p>Non vendiamo i tuoi dati personali.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold">5. Trasferimenti internazionali</h2>
            <p>
              Alcuni fornitori possono trattare dati al di fuori dello Spazio Economico Europeo. In tali casi utilizziamo garanzie adeguate previste dal GDPR (es. Clausole Contrattuali Standard, decisioni di adeguatezza).
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold">6. Conservazione</h2>
            <p>
              Conserviamo i dati per il tempo strettamente necessario alle finalità sopra descritte o quanto richiesto da obblighi di legge (es. fatture per obblighi fiscali). Alla cessazione dell'account i dati vengono eliminati o anonimizzati, salvo obblighi di legge.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold">7. I tuoi diritti (GDPR)</h2>
            <p>Hai diritto ad accedere, rettificare, cancellare o portare i tuoi dati, limitare od opporti al trattamento, revocare il consenso e proporre reclamo al Garante per la protezione dei dati personali. Risponderemo alle tue richieste entro un mese.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold">8. Sicurezza</h2>
            <p>Adottiamo misure tecniche e organizzative adeguate: cifratura in transito, controlli d'accesso, autenticazione, monitoraggio, backup e principi di minimizzazione dei dati.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold">9. Cookie e tecnologie simili</h2>
            <p>
              Utilizziamo cookie essenziali per il funzionamento del servizio (autenticazione, preferenze).
              Cookie analitici o di marketing, se attivati, richiederanno il tuo consenso. Puoi gestire le preferenze dal browser.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold">10. Minori</h2>
            <p>Lumen è pensato per studenti dai 14 anni in su. I minorenni devono utilizzare il servizio con il consenso di un genitore o tutore.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold">11. Modifiche a questa informativa</h2>
            <p>Aggiorneremo questa informativa quando necessario. In caso di modifiche sostanziali te lo comunicheremo con ragionevole anticipo.</p>
          </section>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}